import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Copyright from '../internals/components/Copyright';
import MapViewFlood from './MapViewFlood';
import StatCardHazard from './StatCardHazard';
import CardAlertHazard from './CardAlertHazard';
import ImpactedAreasList from './ImpactedAreasList';
import { authFetch } from '../authFetch';

export default function Hazards() {
  // State to hold our fetched data and calculated stats
  const [floodData, setFloodData] = useState(null);
  const [satelliteDate, setSatelliteDate] = useState('');
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // **NEW**: State to manage the selected area for map interaction
  const [selectedArea, setSelectedArea] = useState(null);

  // **NEW**: Callback function to be passed to the list component
  const handleAreaSelect = useCallback((area) => {
    // If the same area is clicked again, deselect it. Otherwise, select the new one.
    setSelectedArea(prev => (prev && prev.id === area.id ? null : area));
  }, []);

  useEffect(() => {
    // --- FIX START: Add a flag to track if the component is mounted ---
    let isMounted = true;

    // Fetch data from your Django API when the component mounts
    authFetch('/api/flood-data/')
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(apiResponse => {
        // --- FIX: Before updating state, check if the component is still mounted. ---
        if (isMounted) {
            const features = apiResponse.data.features;

            // Calculate statistics from the data
            let highCount = 0;
            let moderateCount = 0;
            let lowCount = 0;

            features.forEach(feature => {
                const riskLevel = feature.properties.risk_level?.toLowerCase();
                if (riskLevel === 'high') highCount++;
                else if (riskLevel === 'moderate') moderateCount++;
                else if (riskLevel === 'low') lowCount++;
            });

            // Create the data structure for our StatCards
            const calculatedStats = [
                { title: 'High risk areas', value: highCount.toLocaleString() },
                { title: 'Moderate risk areas', value: moderateCount.toLocaleString() },
                { title: 'Low risk areas', value: lowCount.toLocaleString() },
            ];

            // Update state with the fetched and processed data
            setStats(calculatedStats);
            setFloodData(apiResponse.data);
            setSatelliteDate(apiResponse.satellite_data_date);
        }
      })
      .catch(err => {
        console.error("Failed to fetch flood data:", err);
        // --- FIX: Check the flag in your error handling logic. ---
        if (isMounted) {
            setError("Could not load flood risk data. Please ensure the backend server is running.");
        }
      })
      .finally(() => {
        // --- FIX: Also check the flag in the finally block. ---
        if (isMounted) {
            setLoading(false);
        }
      });

    // --- FIX: The cleanup function will run when the component unmounts. ---
    return () => {
        isMounted = false;
    };
    // --- FIX END ---
  }, []); // Empty dependency array means this runs once on mount

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>{error}</Typography>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Hazards
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={2} columns={12} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <CardAlertHazard conditions="low" />
        </Grid>
        {/* Map over the calculated stats to display them */}
        {stats.map((card, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCardHazard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Map Section */}
      <Box sx={{ mt: 4 }}>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Flood risk map (contains Copernicus Emergency Management Service information from {satelliteDate})
        </Typography>
        {/* **MODIFIED**: New Grid layout for map and list */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <MapViewFlood 
              floodData={floodData} 
              satelliteDate={satelliteDate}
              selectedArea={selectedArea} // <-- PASS THE SELECTED AREA TO THE MAP
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ImpactedAreasList 
              floodData={floodData}
              onAreaSelect={handleAreaSelect} // <-- PASS THE CALLBACK
              selectedAreaId={selectedArea ? selectedArea.id : null} // <-- PASS THE ID FOR HIGHLIGHTING
            />
          </Grid>
        </Grid>
      </Box>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}